import { differenceInDays } from "date-fns";

// @TODO WEB-4923: Standardize date conversion using date-fns utils

/**
 * Create an array of dates from start to the end date
 *
 * @param {String} startDate - Date string
 * @param {String} endDate - Date string
 * @returns {Object[{isDown, date}]} - Array with days initial state inbluding isDown and date keys
 */
const initUptimeStatus = (startDate, endDate) => {
  const dayCount = differenceInDays(new Date(endDate), new Date(startDate))
  const daysArr = Array.from({ length: dayCount + 1 }, (_, i) => {
    const newDate = new Date(startDate);
    const start = new Date(startDate);
    newDate.setDate(start.getDate() + i);
    return newDate;
  });
  const daysInitilaState = daysArr.map((day) => ({
    isDown: false,
    date: day.toISOString().split("T")[0], // Only get the part YYYY-MM-DD,
  }));
  return daysInitilaState;
}

/**
 * Format minutes to human readable string
 *
 * @param {Number} minutes - minutes value 
 * @returns {String} - human readable string that express time
 */
const formatMinutesofDowntime = (minutes) => {
  if (minutes < 1) return "investigating";

  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
  if (hours > 0) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
  if (mins > 0) parts.push(`${mins} minute${mins > 1 ? "s" : ""}`);

  return `${parts.join(", ")} of downtime`;
}

/**
 * Set log status in corresponding date
 * Note: this function mutates `uptimeStatusPerDay` array
 *
 * @param {Object[]} uptimeStatusPerDay 
 * @param {String} dateStr - Date string 
 * @param {Number} logMinutesOfDowntime - Minutes of downtime
 */
const setDaysLog = (uptimeStatusPerDay, dateStr, logMinutesOfDowntime) => {
  // Add or Update
  let downtime = logMinutesOfDowntime;

  const dayIndex = uptimeStatusPerDay.findIndex((d) => d.date === dateStr);

  if (!dayIndex) {
    // Day Index not found
    return;
  }

  if (uptimeStatusPerDay[dayIndex]?.isDown) {
    downtime = uptimeStatusPerDay[dayIndex].downtimeDuration + logMinutesOfDowntime;
  }

  uptimeStatusPerDay[dayIndex] = {
    isDown: true,
    date: dateStr,
    downtimeDuration: downtime,
    formattedDowntime: formatMinutesofDowntime(downtime),
  };
}

/**
 * Convert date into local date string
 * @TODO replace this with date-fn utilities
 *
 * @param {Date} date - date to convert
 * @returns {String} - new date string
 */
const toLocalISOString = (date) => {
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 6000
  );
  return adjustedDate.toISOString().split("T")[0];
}

/**
 * Get Uptime status data to populate the Uptime component
 * @param {String} startDate - Start date to display the uptime logs
 * @param {String} endDate - End date to display the uptime logs
 * @param {Object[]} logs - Incidents logs
 * @returns {Object[]} - Uptime status data per day
 */
const getUptimeStatus = (startDate, endDate, logs) => {
  if (!logs) {
    return [];
  }
  // Days array with initial state isDown: false
  const uptimeStatusPerDay = initUptimeStatus(startDate, endDate)

  for (const log of logs) {
    const incidentStartDateStr = log.dateISOString.split("T")[0];
    const incidentStartDate = new Date(incidentStartDateStr);

    // look for the resolved date - date format is different here
    const resolveLog = log.items
      .find((item) => item.status === "RESOLVED")
      ?.date?.split(",")[0];

    // No resolve found - mark it all as isDown till the props.endDate
    if (!resolveLog || !log.resolved) {
      const endDateStr = endDate.split("T")[0];
      const endDate = new Date(endDateStr);
      // Plus 1 to include last day for marking if the issue is persisting
      const daysToBeMarked =
        (endDate - incidentStartDate) / (1000 * 3600 * 24) + 1;
      for (let i = 0; i < daysToBeMarked; i++) {
        const newDate = new Date(incidentStartDate);
        newDate.setDate(incidentStartDate.getDate() + i);
        const dateStr = newDate.toISOString().split("T")[0];

        // Mark the days as is down
        setDaysLog(uptimeStatusPerDay, dateStr, log.downtimeDuration);
      }
      continue;
    }

    const resolveLogDate = new Date(resolveLog);
    const incidentResolveDateStr = toLocalISOString(resolveLogDate);
    const incidentResolveDate = new Date(incidentResolveDateStr);

    // Already mark the day as isDown if it is resolved on the same day
    if (incidentResolveDate - incidentStartDate <= 0) {
      setDaysLog(uptimeStatusPerDay, incidentStartDateStr, log.downtimeDuration);
    } else {
      const daysToBeMarked =
        (incidentResolveDate - incidentStartDate) / (1000 * 3600 * 24) + 1;
      for (let i = 0; i <= daysToBeMarked; i++) {
        const newDate = new Date(incidentStartDate);
        newDate.setDate(incidentStartDate.getDate() + i);
        const dateStr = newDate.toISOString().split("T")[0];

        // Mark the days as is down
        setDaysLog(uptimeStatusPerDay, dateStr, log.downtimeDuration);
      }
    }
  }

  return uptimeStatusPerDay
}

export { getUptimeStatus }